import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  InputAdornment,
  Fade,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { Edit, Save, Cancel, Lock, Person, Email, History } from '@mui/icons-material';
import { useAuth } from '../components/context/AuthContext';
import EmailHistory from '../components/EmailHistory';

// Custom MUI Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      dark: '#5a6fd8',
    },
    secondary: {
      main: '#764ba2',
      dark: '#6a4190',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#4b5563',
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2 },
    h6: { fontSize: '1.25rem', fontWeight: 600 },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.85rem', lineHeight: 1.5 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          padding: '8px 20px',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
        },
      },
    },
  },
});

const ProfilePage = React.memo(() => {
  const { user, updateProfile, changePassword, error, setError } = useAuth();

  // Remove editMode state, Edit Profile button, and form submission for updating name/email.
  // Make the name and email fields always disabled (read-only).
  const [activeTab, setActiveTab] = useState(0);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Email config state
  const [emailConfig, setEmailConfig] = useState({
    senderEmail: '',
    senderPassword: '',
    emailService: 'gmail',
    isConfigured: false,
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailConfigSuccess, setEmailConfigSuccess] = useState('');
  const [emailConfigError, setEmailConfigError] = useState('');

  const handleProfileChange = useCallback(
    (e) => {
      setProfileData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
      if (error) setError(null);
      if (profileSuccess) setProfileSuccess('');
    },
    [error, setError, profileSuccess]
  );

  const handlePasswordChange = useCallback(
    (e) => {
      setPasswordData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
      if (passwordError) setPasswordError('');
    },
    [passwordError]
  );

  const handleEmailConfigChange = useCallback(
    (e) => {
      setEmailConfig((prev) => ({ ...prev, [e.target.name]: e.target.value }));
      if (emailConfigError) setEmailConfigError('');
      if (emailConfigSuccess) setEmailConfigSuccess('');
    },
    [emailConfigError, emailConfigSuccess]
  );

  const validateProfile = useCallback(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!profileData.name.trim() || profileData.name.length < 2) {
      setError('Name must be at least 2 characters long');
      return false;
    }
    if (!emailRegex.test(profileData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  }, [profileData, setError]);

  const validatePassword = useCallback(() => {
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return false;
    }
    return true;
  }, [passwordData]);

  const validateEmailConfig = useCallback(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailConfig.senderEmail || !emailRegex.test(emailConfig.senderEmail)) {
      setEmailConfigError('Please enter a valid sender email');
      return false;
    }
    if (!emailConfig.isConfigured && !emailConfig.senderPassword) {
      setEmailConfigError('Sender password is required for initial configuration');
      return false;
    }
    return true;
  }, [emailConfig]);

  const handleProfileSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateProfile()) return;

      // setLoading(true); // This line is no longer needed
      // const result = await updateProfile(profileData.name, profileData.email); // This line is no longer needed
      // setLoading(false); // This line is no longer needed

      // if (result.success) { // This block is no longer needed
      //   setEditMode(false);
      //   setProfileSuccess('Profile updated successfully!');
      //   setTimeout(() => setProfileSuccess(''), 3000);
      // }
    },
    [profileData, updateProfile, validateProfile]
  );

  const handlePasswordSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validatePassword()) return;

      setPasswordLoading(true);
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordLoading(false);

      if (result.success) {
        setShowPasswordDialog(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setPasswordError(result.message || 'Failed to update password');
      }
    },
    [passwordData, changePassword, validatePassword]
  );

  const handleEmailConfigSave = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateEmailConfig()) return;

      setEmailConfigLoading(true);
      try {
        const res = await fetch('/api/auth/email-config', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            senderEmail: emailConfig.senderEmail,
            senderPassword: emailConfig.senderPassword,
            emailService: emailConfig.emailService,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setEmailConfigSuccess('Email credentials updated successfully!');
          setEmailConfig((prev) => ({
            ...prev,
            senderPassword: '',
            isConfigured: true,
          }));
          setTimeout(() => setEmailConfigSuccess(''), 3000);
        } else {
          setEmailConfigError(data.message || 'Failed to update credentials');
        }
      } catch (err) {
        setEmailConfigError('Network error. Please try again.');
      } finally {
        setEmailConfigLoading(false);
      }
    },
    [emailConfig, validateEmailConfig]
  );

  const cancelEdit = useCallback(() => {
    // setProfileData({ // This line is no longer needed
    //   name: user?.name || '',
    //   email: user?.email || '',
    // });
    // setEditMode(false); // This line is no longer needed
    setError(null);
    setProfileSuccess('');
  }, [user, setError]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  useEffect(() => {
    async function fetchEmailConfig() {
      setEmailConfigLoading(true);
      setEmailConfigError('');
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        if (data.success && data.data && data.data.emailConfig) {
          setEmailConfig({
            senderEmail: data.data.emailConfig.senderEmail || '',
            senderPassword: '',
            emailService: data.data.emailConfig.emailService || 'gmail',
            isConfigured: data.data.emailConfig.isConfigured || false,
          });
        }
      } catch (err) {
        setEmailConfigError('Failed to fetch email config');
      } finally {
        setEmailConfigLoading(false);
      }
    }
    fetchEmailConfig();
  }, []);

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} color="primary" />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          py: { xs: 4, sm: 6 },
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #e0e7ff 0%, #d1fae5 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle Background Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
            zIndex: 0,
            opacity: 0.6,
          }}
        />

        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 4,
                textAlign: 'center',
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              }}
            >
              Profile Settings
            </Typography>
          </Fade>

          {/* Tabs */}
          <Fade in timeout={1000}>
            <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Profile navigation tabs"
                sx={{
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.main',
                    height: 3,
                  },
                }}
              >
                <Tab icon={<Person />} iconPosition="start" label="Profile" aria-label="Profile settings" />
                <Tab icon={<History />} iconPosition="start" label="Email History" aria-label="Email history" />
              </Tabs>
            </Paper>
          </Fade>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Profile Information */}
              <Grid item xs={12} md={8}>
                <Fade in timeout={1200}>
                  <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          width: { xs: 56, sm: 72 },
                          height: { xs: 56, sm: 72 },
                          bgcolor: 'primary.main',
                          fontSize: { xs: '1.25rem', sm: '1.75rem' },
                          mr: 2,
                        }}
                      >
                        {user.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                        >
                          {user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>

                    {(error || profileSuccess) && (
                      <Fade in timeout={500}>
                        <Alert
                          severity={profileSuccess ? 'success' : 'error'}
                          sx={{ mb: 2, borderRadius: 1.5 }}
                        >
                          {profileSuccess || error}
                        </Alert>
                      </Fade>
                    )}

                    <Box component="form" onSubmit={handleProfileSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={profileData.name}
                            onChange={handleProfileChange}
                            disabled // Always disabled
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                            sx={{ mb: { xs: 1, sm: 0 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={profileData.email}
                            onChange={handleProfileChange}
                            disabled // Always disabled
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* The Edit Profile button is removed */}
                        {/* The Save Changes button is removed */}
                        {/* The Cancel button is removed */}
                      </Box>
                    </Box>

                    {/* Email Credentials Section */}
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                      Email Sending Credentials
                    </Typography>
                    <Box component="form" onSubmit={handleEmailConfigSave} autoComplete="off">
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Sender Email"
                            name="senderEmail"
                            value={emailConfig.senderEmail}
                            onChange={handleEmailConfigChange}
                            fullWidth
                            required
                            size="small"
                            autoComplete="off"
                            InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="App Password"
                            name="senderPassword"
                            value={emailConfig.senderPassword}
                            onChange={handleEmailConfigChange}
                            type="password"
                            fullWidth
                            required={!emailConfig.isConfigured}
                            size="small"
                            autoComplete="new-password"
                            helperText={emailConfig.isConfigured ? 'Leave blank to keep existing password' : ''}
                            InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Email Service"
                            name="emailService"
                            value={emailConfig.emailService}
                            onChange={handleEmailConfigChange}
                            select
                            fullWidth
                            size="small"
                            SelectProps={{ native: true }}
                            InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                          >
                            <option value="gmail">Gmail</option>
                            <option value="outlook">Outlook</option>
                            <option value="yahoo">Yahoo</option>
                            <option value="smtp">Custom SMTP</option>
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={emailConfigLoading}
                            sx={{
                              fontSize: { xs: '0.85rem', sm: '0.9rem' },
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            }}
                          >
                            {emailConfigLoading ? (
                              <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : emailConfig.isConfigured ? (
                              'Update Credentials'
                            ) : (
                              'Save Credentials'
                            )}
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          {(emailConfigError || emailConfigSuccess) && (
                            <Fade in timeout={500}>
                              <Alert
                                severity={emailConfigSuccess ? 'success' : 'error'}
                                sx={{ mt: 2, borderRadius: 1.5 }}
                              >
                                {emailConfigSuccess || emailConfigError}
                              </Alert>
                            </Fade>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Fade>
              </Grid>

              {/* Account Actions */}
              <Grid item xs={12} md={4}>
                <Fade in timeout={1200}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        Account Actions
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setShowPasswordDialog(true)}
                        startIcon={<Lock />}
                        sx={{
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          py: 1.5,
                          borderColor: 'rgba(0,0,0,0.2)',
                          color: 'text.secondary',
                          '&:hover': { background: 'rgba(0,0,0,0.05)' },
                        }}
                        aria-label="Change password"
                      >
                        Change Password
                      </Button>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                      >
                        Member since: {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                      >
                        Last login: {new Date(user.lastLogin).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            </Grid>
          )}

          {/* Email History Tab */}
          {activeTab === 1 && <EmailHistory />}

          {/* Password Change Dialog */}
          <Dialog
            open={showPasswordDialog}
            onClose={() => setShowPasswordDialog(false)}
            maxWidth="xs"
            fullWidth
            fullScreen={window.innerWidth < 600}
            aria-labelledby="change-password-dialog-title"
          >
            <DialogTitle id="change-password-dialog-title" sx={{ fontWeight: 600 }}>
              Change Password
            </DialogTitle>
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <DialogContent>
                {passwordError && (
                  <Fade in timeout={500}>
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                      {passwordError}
                    </Alert>
                  </Fade>
                )}
                <TextField
                  fullWidth
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  size="small"
                  sx={{ mb: 2 }}
                  InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                />
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  size="small"
                  sx={{ mb: 2 }}
                  InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  size="small"
                  InputLabelProps={{ sx: { fontFamily: "'Inter', sans-serif" } }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setShowPasswordDialog(false)}
                  disabled={passwordLoading}
                  sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, color: 'text.secondary' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={passwordLoading}
                  startIcon={passwordLoading ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  }}
                >
                  Update Password
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
});

export default ProfilePage;